import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './common-iframe.css';

const isPdfSource = (url = '') => /\.pdf($|[?#])/i.test(String(url).split('#')[0]);

const isMobileClient = () => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobile/i.test(
    navigator.userAgent || ''
  );
};

const resolveIframeSrc = (url = '') => {
  if (!url || !isPdfSource(url) || !isMobileClient()) {
    return url;
  }

  // Mobile browsers/WebViews usually cannot render PDF directly in an iframe.
  // Google Docs viewer is used as a mobile-safe embed fallback.
  const cleanPdfUrl = String(url).split('#')[0];
  return `https://docs.google.com/gview?embedded=true&url=${encodeURIComponent(cleanPdfUrl)}`;
};

const IframeComponent = ({ iframeSrc, fillViewport = false }) => {
  const navigate = useNavigate();
  const iframeRef = useRef(null);
  const cleanupRef = useRef(() => {});
  const loadingTimeoutRef = useRef(null);
  const settleTimeoutsRef = useRef([]);
  const measuredHeightRef = useRef(null);
  const [height, setHeight] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [useIframeScrollFallback, setUseIframeScrollFallback] = useState(false);
  const resolvedIframeSrc = resolveIframeSrc(iframeSrc);
  const isPdfOnMobile = isPdfSource(iframeSrc) && isMobileClient();

  const applyHiddenScrollbarStyles = (iframeDocument) => {
    if (!iframeDocument) {
      return;
    }

    try {
      const styleId = 'iframe-hidden-scrollbar-style';
      const styleTarget = iframeDocument.head || iframeDocument.documentElement;

      if (iframeDocument.documentElement) {
        iframeDocument.documentElement.style.msOverflowStyle = 'none';
        iframeDocument.documentElement.style.scrollbarWidth = 'none';
      }

      if (iframeDocument.body) {
        iframeDocument.body.style.msOverflowStyle = 'none';
        iframeDocument.body.style.scrollbarWidth = 'none';
      }

      if (!styleTarget || iframeDocument.getElementById(styleId)) {
        return;
      }

      const styleElement = iframeDocument.createElement('style');
      styleElement.id = styleId;
      styleElement.textContent = `
        html, body {
          -ms-overflow-style: none !important;
          scrollbar-width: none !important;
        }

        html::-webkit-scrollbar,
        body::-webkit-scrollbar,
        *::-webkit-scrollbar {
          width: 0 !important;
          height: 0 !important;
          display: none !important;
          background: transparent !important;
        }
      `;
      styleTarget.appendChild(styleElement);
    } catch (error) {
      // Ignore if the iframe document cannot be styled.
    }
  };

  const updateHeight = (value) => {
    const parsedHeight = Number(value);
    if (!Number.isNaN(parsedHeight) && parsedHeight > 0) {
      const newHeight = parsedHeight + 96;
      measuredHeightRef.current = newHeight;
      setHeight((previousHeight) => (previousHeight !== newHeight ? newHeight : previousHeight));
      setUseIframeScrollFallback(false);
    }
  };

  const getIframeContentHeight = (iframeDocument) => {
    if (!iframeDocument) {
      return 0;
    }

    const { body, documentElement } = iframeDocument;
    const iframeWindow = iframeDocument.defaultView;
    const baseHeight = Math.max(
      body?.scrollHeight || 0,
      body?.offsetHeight || 0,
      body?.clientHeight || 0,
      documentElement?.scrollHeight || 0,
      documentElement?.offsetHeight || 0,
      documentElement?.clientHeight || 0
    );

    let deepestElementBottom = 0;
    let deepestElementMarginBottom = 0;
    let bodyMarginBottom = 0;
    let htmlMarginBottom = 0;
    let deepestScrollableBottom = 0;

    try {
      if (iframeWindow && body) {
        bodyMarginBottom = parseFloat(iframeWindow.getComputedStyle(body).marginBottom) || 0;
      }
      if (iframeWindow && documentElement) {
        htmlMarginBottom = parseFloat(iframeWindow.getComputedStyle(documentElement).marginBottom) || 0;
      }

      const elements = iframeDocument.querySelectorAll('body *');
      elements.forEach((element) => {
        const rect = element.getBoundingClientRect();
        const elementBottom = rect.bottom + (iframeDocument.defaultView?.scrollY || 0);
        const elementMarginBottom =
          parseFloat(iframeDocument.defaultView?.getComputedStyle(element).marginBottom || 0) || 0;
        const elementTop = rect.top + (iframeWindow?.scrollY || 0);
        const scrollableBottom =
          elementTop +
          Math.max(element.scrollHeight || 0, element.offsetHeight || 0, element.clientHeight || 0) +
          elementMarginBottom;

        if (elementBottom > deepestElementBottom) {
          deepestElementBottom = elementBottom;
          deepestElementMarginBottom = elementMarginBottom;
        }

        if (scrollableBottom > deepestScrollableBottom) {
          deepestScrollableBottom = scrollableBottom;
        }
      });
    } catch (error) {
      // Fall back to the document height values above.
    }

    return Math.max(
      baseHeight,
      body?.getBoundingClientRect?.().bottom || 0,
      documentElement?.getBoundingClientRect?.().bottom || 0,
      deepestElementBottom + deepestElementMarginBottom,
      baseHeight + bodyMarginBottom + htmlMarginBottom,
      deepestScrollableBottom
    );
  };

  const syncIframeDocumentHeight = () => {
    if (fillViewport) {
      return;
    }
    try {
      const iframeDocument = iframeRef.current?.contentWindow?.document;
      if (!iframeDocument) {
        return;
      }

      applyHiddenScrollbarStyles(iframeDocument);

      if (iframeDocument.documentElement) {
        iframeDocument.documentElement.style.overflowY = 'hidden';
      }
      if (iframeDocument.body) {
        iframeDocument.body.style.overflowY = 'hidden';
      }

      const contentHeight = getIframeContentHeight(iframeDocument);
      updateHeight(contentHeight);
      if (contentHeight > 0) {
        setIsLoading(false);
      } else {
        setUseIframeScrollFallback(true);
      }
    } catch (error) {
      // Ignore cross-origin iframe read errors and rely on postMessage/fallback height.
      setUseIframeScrollFallback(true);
    }
  };

  useEffect(() => {
    if (!iframeSrc) {
      navigate('/home');
    }
  }, [iframeSrc, navigate]);

  useEffect(() => {
    cleanupRef.current();
    settleTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    settleTimeoutsRef.current = [];

    if (loadingTimeoutRef.current) {
      window.clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    setIsLoading(true);
    setHeight(null);
    measuredHeightRef.current = null;
    setUseIframeScrollFallback(false);
  }, [iframeSrc]);

  useEffect(() => {
    const handleWindowResize = () => {
      syncIframeDocumentHeight();
    };

    const handleMessage = (event) => {
      if (event.source && iframeRef.current?.contentWindow && event.source !== iframeRef.current.contentWindow) {
        return;
      }

      let frameHeightValue = null;
      if (event == null) return;
      // Support numeric/string payloads as well as objects
      // { iframeHeight, frameHeight, height } and { type: 'iframeHeight', height }.
      if (typeof event.data === 'number' || typeof event.data === 'string') {
        frameHeightValue = event.data;
      } else if (typeof event.data === 'object' && event.data !== null) {
        frameHeightValue =
          event.data.frameHeight ||
          event.data.iframeHeight ||
          event.data.height ||
          (event.data.type === 'iframeHeight' ? event.data.height : null);
      }

      if (frameHeightValue && !fillViewport) {
        updateHeight(frameHeightValue);
        setIsLoading(false);
        if (loadingTimeoutRef.current) {
          window.clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
      }
    };

    window.addEventListener('resize', handleWindowResize);
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('resize', handleWindowResize);
      window.removeEventListener('message', handleMessage);
    };
  }, [fillViewport]);

  const handleLoad = () => {
    window.scrollTo(0, 0);
    if (fillViewport || isPdfOnMobile) {
      setIsLoading(false);
      if (isPdfOnMobile && !fillViewport) {
        setUseIframeScrollFallback(true);
      }
      return;
    }
    cleanupRef.current();
    settleTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
    settleTimeoutsRef.current = [];

    if (loadingTimeoutRef.current) {
      window.clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }

    syncIframeDocumentHeight();

    try {
      const iframeDocument = iframeRef.current?.contentWindow?.document;
      if (!iframeDocument) {
        loadingTimeoutRef.current = window.setTimeout(() => {
          setIsLoading(false);
          setUseIframeScrollFallback(true);
          loadingTimeoutRef.current = null;
        }, 4000);
        return;
      }

      const observedNodes = [iframeDocument.documentElement, iframeDocument.body].filter(Boolean);
      if (observedNodes.length === 0) {
        return;
      }

      applyHiddenScrollbarStyles(iframeDocument);

      const mutationObserver = new MutationObserver(syncIframeDocumentHeight);
      observedNodes.forEach((node) => {
        mutationObserver.observe(node, {
          attributes: true,
          childList: true,
          subtree: true,
          characterData: true
        });
      });

      let resizeObserver = null;
      if (typeof ResizeObserver !== 'undefined') {
        resizeObserver = new ResizeObserver(syncIframeDocumentHeight);
        observedNodes.forEach((node) => resizeObserver.observe(node));
      }

      if (iframeDocument.documentElement) {
        iframeDocument.documentElement.style.overflowY = 'hidden';
      }
      if (iframeDocument.body) {
        iframeDocument.body.style.overflowY = 'hidden';
      }

      const intervalId = window.setInterval(syncIframeDocumentHeight, 350);

      try {
        const scriptContent = `(function(){if(window.__iframeHeightSyncAttached){try{window.__iframeHeightSendHeight();}catch(e){}return;}function hideScrollbar(){try{if(document.documentElement){document.documentElement.style.msOverflowStyle='none';document.documentElement.style.scrollbarWidth='none';}if(document.body){document.body.style.msOverflowStyle='none';document.body.style.scrollbarWidth='none';}if(!document.getElementById('iframe-hidden-scrollbar-style')){var style=document.createElement('style');style.id='iframe-hidden-scrollbar-style';style.textContent="html, body { -ms-overflow-style:none !important; scrollbar-width:none !important; } html::-webkit-scrollbar, body::-webkit-scrollbar, *::-webkit-scrollbar { width:0 !important; height:0 !important; display:none !important; background:transparent !important; }";(document.head||document.documentElement).appendChild(style);}}catch(e){}}function lockOverflow(){try{if(document.documentElement){document.documentElement.style.overflowY='hidden';}if(document.body){document.body.style.overflowY='hidden';}}catch(e){}}function num(v){var n=parseFloat(v);return Number.isFinite(n)?n:0;}function getDeepHeight(){var b=document.body,h=document.documentElement;var maxHeight=Math.max((b&&b.scrollHeight)||0,(b&&b.offsetHeight)||0,(b&&b.clientHeight)||0,(h&&h.scrollHeight)||0,(h&&h.offsetHeight)||0,(h&&h.clientHeight)||0,(b&&b.getBoundingClientRect&&b.getBoundingClientRect().bottom)||0,(h&&h.getBoundingClientRect&&h.getBoundingClientRect().bottom)||0);try{if(b){maxHeight=Math.max(maxHeight,maxHeight+num(getComputedStyle(b).marginBottom));}if(h){maxHeight=Math.max(maxHeight,maxHeight+num(getComputedStyle(h).marginBottom));}var nodes=document.querySelectorAll('body *');for(var i=0;i<nodes.length;i+=1){var rect=nodes[i].getBoundingClientRect();var marginBottom=num(getComputedStyle(nodes[i]).marginBottom);var absoluteTop=rect.top+(window.scrollY||0);var visibleBottom=rect.bottom+(window.scrollY||0)+marginBottom;var scrollableBottom=absoluteTop+Math.max(nodes[i].scrollHeight||0,nodes[i].offsetHeight||0,nodes[i].clientHeight||0)+marginBottom;if(visibleBottom>maxHeight){maxHeight=visibleBottom;}if(scrollableBottom>maxHeight){maxHeight=scrollableBottom;}}}catch(e){}return Math.ceil(maxHeight);}function sendHeight(){try{hideScrollbar();lockOverflow();parent.postMessage({type:'iframeHeight',height:getDeepHeight()},'*');}catch(e){}}window.__iframeHeightSyncAttached=true;window.__iframeHeightSendHeight=sendHeight;hideScrollbar();lockOverflow();var nodes=[document.documentElement,document.body].filter(Boolean);try{nodes.forEach(function(node){new MutationObserver(sendHeight).observe(node,{childList:true,subtree:true,attributes:true,characterData:true});});}catch(e){}if(window.ResizeObserver){try{var ro=new ResizeObserver(sendHeight);nodes.forEach(function(node){ro.observe(node);});}catch(e){}}window.addEventListener('load',sendHeight);window.addEventListener('resize',sendHeight);setTimeout(sendHeight,150);setTimeout(sendHeight,600);setTimeout(sendHeight,1500);setTimeout(sendHeight,3000);sendHeight();})();`;
        const scriptEl = iframeDocument.createElement('script');
        scriptEl.type = 'text/javascript';
        scriptEl.text = scriptContent;
        (iframeDocument.head || iframeDocument.documentElement).appendChild(scriptEl);
      } catch (e) {
        // If script injection is blocked, the parent observers still keep syncing height.
      }

      [100, 300, 700, 1200, 2000, 3500, 5000].forEach((delay) => {
        const timeoutId = window.setTimeout(syncIframeDocumentHeight, delay);
        settleTimeoutsRef.current.push(timeoutId);
      });

      cleanupRef.current = () => {
        mutationObserver.disconnect();
        resizeObserver?.disconnect();
        window.clearInterval(intervalId);
        settleTimeoutsRef.current.forEach((timeoutId) => window.clearTimeout(timeoutId));
        settleTimeoutsRef.current = [];
        if (loadingTimeoutRef.current) {
          window.clearTimeout(loadingTimeoutRef.current);
          loadingTimeoutRef.current = null;
        }
      };

      loadingTimeoutRef.current = window.setTimeout(() => {
        syncIframeDocumentHeight();
        setIsLoading(false);
        if (!measuredHeightRef.current) {
          setUseIframeScrollFallback(true);
        }
        loadingTimeoutRef.current = null;
      }, 1500);
    } catch (error) {
      // Ignore cross-origin iframe read errors and rely on postMessage/fallback height.
      setIsLoading(false);
      setUseIframeScrollFallback(true);
    }
  };

  useEffect(() => () => cleanupRef.current(), []);

  const iframeHeight = fillViewport
    ? '100%'
    : height
      ? `${height}px`
      : useIframeScrollFallback || isPdfOnMobile
        ? '2200px'
        : 'calc(100vh - 120px)';
  const iframeOverflow = fillViewport || isPdfOnMobile ? 'auto' : 'hidden';
  const iframeScrolling = fillViewport || isPdfOnMobile ? 'yes' : 'no';
  const scrollbarGutterSize = 18;
  const iframeWidth = fillViewport || isPdfOnMobile ? '100%' : `calc(100% + ${scrollbarGutterSize}px)`;
  const iframeMarginRight = fillViewport || isPdfOnMobile ? '0' : `-${scrollbarGutterSize}px`;

  const skeleton = (
    <div
      className={`iframe-skeleton ${fillViewport ? 'iframe-skeleton--fill-overlay' : ''}`}
      role="status"
      aria-live="polite"
      aria-label="Loading application content"
    >
      <div className="skeleton-header skeleton-shimmer" />
      <div className="skeleton-toolbar">
        <span className="skeleton-pill skeleton-shimmer" />
        <span className="skeleton-pill skeleton-shimmer" />
        <span className="skeleton-pill skeleton-shimmer" />
      </div>
      <div className="skeleton-main">
        <div className="skeleton-column">
          <span className="skeleton-line skeleton-shimmer" />
          <span className="skeleton-line skeleton-shimmer" />
          <span className="skeleton-line short skeleton-shimmer" />
        </div>
        <div className="skeleton-column">
          <span className="skeleton-line skeleton-shimmer" />
          <span className="skeleton-line medium skeleton-shimmer" />
          <span className="skeleton-line skeleton-shimmer" />
        </div>
      </div>
      <p className="skeleton-label">Preparing your application workspace...</p>
    </div>
  );

  return (
    <div className={`iframe-wrapper ${fillViewport ? 'iframe-wrapper--fill' : ''}`}>
      {!fillViewport && isLoading && skeleton}
      <div className={`iframe-viewport ${fillViewport ? 'iframe-viewport--fill' : ''}`}>
        {fillViewport && isLoading && skeleton}
        <iframe
          className="iframe-element"
          ref={iframeRef}
          src={resolvedIframeSrc}
          onLoad={handleLoad}
          style={{
            width: iframeWidth,
            height: iframeHeight,
            marginRight: iframeMarginRight,
            border: 'none',
            display: 'block',
            overflow: iframeOverflow,
            msOverflowStyle: fillViewport || isPdfOnMobile ? undefined : 'none',
            scrollbarWidth: fillViewport || isPdfOnMobile ? undefined : 'none',
            opacity: fillViewport && isLoading ? 0 : 1
          }}
          scrolling={iframeScrolling}
          title={fillViewport ? 'Application form' : isPdfOnMobile ? 'PDF Viewer' : 'AutoHeightIframe'}
          allow="fullscreen"
        />
      </div>
    </div>
  );
};

export default IframeComponent;
